import { COLOR_FLYWEIGHT, formatData } from '@utilities';

import { addSlideFooter } from '../slide_footer';
import { cleanQuestionTitle } from '../../../../../../../../../../../pages/Survey/components/Dashboard/utilities/helpers.js';
import { FOOTER_TEXT, TABLE_BORDER, TABLE_FONT_SIZE, TEXT_COLOR, TITLE_TEXT } from '../constants';

/**
 * Creates data table slides for standard numeric question type
 * No matrix/table support, no breakout color logic.
 */
export const createNumericTableSlide = ({
  compareBy,
  displayAs = 'pct',
  pptx,
  questionData,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  title,
}) => {
  const cleanTitle = cleanQuestionTitle(questionTitle);
  const questionPrefix = questionNumber ? `Q${questionNumber}. ` : '';
  const titleWithQuestionNumber = `${questionPrefix}${cleanTitle}`;

  const tableToUse = displayAs === 'pct' ? questionData.tablePct : questionData.tableCount;
  if (!tableToUse?.columns || !tableToUse?.data) return;

  if (compareBy?.value === 'overall') {
    createOverallSlides({
      pptx,
      reportType,
      reportUrl,
      tableToUse,
      title,
      titleWithQuestionNumber,
    });
  } else {
    const breakoutKey = 'compare_by_breakout';
    const dataBySegment = {};
    tableToUse.data.forEach((row) => {
      const segment = row[breakoutKey];
      if (!segment) return;
      if (!dataBySegment[segment]) dataBySegment[segment] = [];
      dataBySegment[segment].push(row);
    });

    Object.entries(dataBySegment).forEach(([segmentName, segmentData]) => {
      const slideTitle = `${titleWithQuestionNumber} Compared by: ${segmentName}`;
      const segmentTable = {
        columns: tableToUse.columns.filter((col) => {
          const accessor = col.accessor || col.accessorKey;
          return accessor !== breakoutKey;
        }),
        data: segmentData,
      };
      createCustomTableSlide({
        pptx,
        reportType,
        reportUrl,
        slideTitle,
        table: segmentTable,
        title,
      });
    });
  }
};

const createOverallSlides = ({
  pptx,
  reportType,
  reportUrl,
  tableToUse,
  title,
  titleWithQuestionNumber,
}) => {
  const rowsPerSlide = 10;
  const dataChunks = [];
  for (let i = 0; i < tableToUse.data.length; i += rowsPerSlide) {
    dataChunks.push(tableToUse.data.slice(i, i + rowsPerSlide));
  }
  dataChunks.forEach((chunkData, chunkIdx) => {
    const slideTitle =
      dataChunks.length > 1
        ? `${titleWithQuestionNumber} (${chunkIdx + 1}/${dataChunks.length})`
        : titleWithQuestionNumber;
    const chunkTable = {
      columns: tableToUse.columns,
      data: chunkData,
    };
    createCustomTableSlide({
      pptx,
      reportType,
      reportUrl,
      slideTitle,
      table: chunkTable,
      title,
    });
  });
};

// Table slide builder (chunked)
const createCustomTableSlide = ({ pptx, reportType, reportUrl, slideTitle, table, title }) => {
  if (!table || !table.columns || !table.data) return;
  const { columns, data } = table;
  const headers = columns.map((col) => col.header || col.Header || col.accessor);
  const rowsPerSlide = 10;
  const dataChunks = [];
  for (let i = 0; i < data.length; i += rowsPerSlide) {
    dataChunks.push(data.slice(i, i + rowsPerSlide));
  }
  dataChunks.forEach((chunkData, chunkIdx) => {
    const slide = pptx.addSlide();
    const finalSlideTitle =
      dataChunks.length > 1 ? `${slideTitle} (${chunkIdx + 1}/${dataChunks.length})` : slideTitle;
    slide.addText(finalSlideTitle, { ...TITLE_TEXT, fontSize: 14 });

    const tableData = chunkData.map((row) =>
      columns.map((col) => {
        const accessor = col.accessor || col.accessorKey;
        return formatData({ format: col.format, value: row[accessor] });
      })
    );

    const TOTAL_TABLE_WIDTH = 9.3;
    const FIRST_COLUMN_RATIO = 0.28;
    const columnCount = headers.length;
    const firstColWidth = TOTAL_TABLE_WIDTH * FIRST_COLUMN_RATIO;
    const remainingColWidth = (TOTAL_TABLE_WIDTH - firstColWidth) / (columnCount - 1 || 1);
    const colWidths = [firstColWidth, ...Array(columnCount - 1).fill(remainingColWidth)];

    const headerRow = headers.map((header) => ({
      options: {
        bold: true,
        color: TEXT_COLOR,
        fill: { color: COLOR_FLYWEIGHT },
        fontSize: TABLE_FONT_SIZE,
      },
      text: header.toUpperCase(),
    }));

    const dataRows = tableData.map((row) =>
      row.map((cell, cIdx) => ({
        options: {
          align: cIdx === 0 ? 'left' : 'right',
          color: TEXT_COLOR,
          fontFace: 'Arial',
          fontSize: TABLE_FONT_SIZE,
          valign: 'middle',
        },
        text: cell,
      }))
    );

    const totalAccessor = columns.find((c) =>
      ['total_respondents', 'respondent_count'].includes(c.accessor)
    );
    if (totalAccessor) {
      const totalResponses = chunkData.reduce(
        (acc, r) => acc + (Number(r[totalAccessor.accessor]) || 0),
        0
      );
      const summaryRow = headers.map((h, i) => ({
        options: {
          align: i === 0 ? 'left' : 'right',
          bold: true,
          border: TABLE_BORDER,
          color: TEXT_COLOR,
          fontFace: 'Arial',
          fontSize: TABLE_FONT_SIZE,
          valign: 'middle',
        },
        text:
          i === 0
            ? 'TOTAL RESPONSES'
            : h === (totalAccessor.Header || totalAccessor.header || totalAccessor.accessor)
              ? totalResponses.toLocaleString()
              : '',
      }));
      dataRows.push(summaryRow);
    }

    slide.addTable([headerRow, ...dataRows], {
      border: TABLE_BORDER,
      colW: colWidths,
      w: '95%',
      x: 0.3,
      y: 1.5,
    });

    addSlideFooter({ pptx, reportType, reportUrl, slide });
    slide.addText(
      [{ text: 'Source: ' }, { options: { hyperlink: { url: reportUrl } }, text: title }],
      FOOTER_TEXT
    );
  });
};
